import { Component, Element, Event, EventEmitter, h, Host, Listen, Method, Prop, State, Watch } from "@stencil/core";

import {
  StzhAccordionItemBlurEvent,
  StzhAccordionItemClosedEvent,
  StzhAccordionItemCloseEvent,
  StzhAccordionItemFocusEvent,
  StzhAccordionItemOpenedEvent,
  StzhAccordionItemOpenEvent,
  StzhAccordionVariant,
  StzhAccordionVariantChangeEvent,
  StzhBadgeType,
} from "../../index";

import { waitForEvent } from "../../utils/event-utils";
import { animateTo, shimKeyframesAutoValues, stopAnimations } from "../../utils/animation-utils";
import { hasSlot } from "../../utils/utils";

const ANIMATION_SHOW = {
  keyframes: [
    { height: "0", opacity: "0" },
    { height: "auto", opacity: "1" },
  ],
  options: { duration: 300, easing: "linear" },
};

const ANIMATION_HIDE = {
  keyframes: [
    { height: "auto", opacity: "1" },
    { height: "0", opacity: "0" },
  ],
  options: { duration: 300, easing: "linear" },
};

let accordionItemCounter = 0;

/**
 * @slot - Slot for any content
 * @slot icon - Slot for icon element
 */
@Component({
  tag: "stzh-accordion-item",
  styleUrl: "stzh-accordion-item.scss",
  shadow: true,
})
export class StzhAccordionItem {
  /** Variant (overwrites variant from accordion parent) */
  @Prop() variant: StzhAccordionVariant;

  /** Whether fold is collapsible (overwrites collapsible from accordion parent) */
  @Prop() collapsible: boolean;

  /** Whether fold is open */
  @Prop({ mutable: true, reflect: true }) open: boolean = false;

  /** Whether fold disabled */
  @Prop({ reflect: true }) disabled: boolean = false;

  /** Heading title */
  @Prop() heading: string = "";

  /** Subtitle */
  @Prop() subtitle: string = "";

  /** Description */
  @Prop() description: string = "";

  /** Icon (use instead of icon slot) */
  @Prop() icon: string = "";

  /** Badge */
  @Prop() badge: string = "";

  /** Whether badge should be displayed empty */
  @Prop({ reflect: true }) badgeEmpty: boolean = false;

  /** Badge type */
  @Prop({ reflect: true }) badgeType: StzhBadgeType = "default";

  /** Heading level, for accessibility, no visual impact */
  @Prop({ reflect: true }) headingLevel: "1" | "2" | "3" | "4" = "3";

  /**
   * For Adobe Analytics.
   * Will be rendered as value of `s-object-id` attribute to the button element.
   * Default value will be taken from `heading` prop.
   */
  @Prop() analyticsId: string;

  @Element() element: HTMLStzhAccordionItemElement;

  /** Open event */
  @Event() stzhOpen: EventEmitter<StzhAccordionItemOpenEvent>;

  /** Opened event (after animation) */
  @Event() stzhOpened: EventEmitter<StzhAccordionItemOpenedEvent>;

  /** Close event */
  @Event() stzhClose: EventEmitter<StzhAccordionItemCloseEvent>;

  /** Closed event (after animation) */
  @Event() stzhClosed: EventEmitter<StzhAccordionItemClosedEvent>;

  @Watch("variant")
  variantWatcher(newValue: StzhAccordionVariant) {
    if (typeof newValue !== "undefined") {
      this._variant = newValue;
    } else if (this.accordion) {
      this._variant = this.accordion.variant;
    }
  }

  @Watch("open")
  async openWatcher(newValue: boolean) {
    if (!this.bodyElement) {
      return;
    }

    if (newValue) {
      if (this.toggledByMethod) {
        this.stzhOpen.emit({
          component: "stzh-accordion-item",
        });
      }

      await stopAnimations(this.bodyElement);
      this.bodyElement.hidden = false;

      await animateTo(
        this.bodyElement,
        shimKeyframesAutoValues(ANIMATION_SHOW.keyframes, {
          height: this.bodyElement.scrollHeight,
        }),
        ANIMATION_SHOW.options,
      );

      this.bodyElement.style.height = "auto";
      this.bodyElement.style.overflow = "visible";

      if (this.toggledByMethod) {
        this.stzhOpened.emit({
          component: "stzh-accordion-item",
        });
      }
    } else {
      if (this.toggledByMethod) {
        this.stzhClose.emit({
          component: "stzh-accordion-item",
        });
      }

      await stopAnimations(this.bodyElement);
      this.bodyElement.style.overflow = "hidden";

      await animateTo(
        this.bodyElement,
        shimKeyframesAutoValues(ANIMATION_HIDE.keyframes, {
          height: this.bodyElement.scrollHeight,
        }),
        ANIMATION_HIDE.options,
      );

      this.bodyElement.hidden = true;
      this.bodyElement.style.height = "0px";

      if (this.toggledByMethod) {
        this.stzhClosed.emit({
          component: "stzh-accordion-item",
        });
      }
    }

    this.toggledByMethod = false;
  }

  /** Show accordion item content */
  @Method()
  async show() {
    if (this.open || this.disabled) {
      return;
    }

    this.toggledByMethod = true;
    this.open = true;
    return waitForEvent(this.element, "stzhOpened");
  }

  /** Hide accordion item content */
  @Method()
  async hide() {
    if (!this.open || this.disabled) {
      return;
    }

    this.toggledByMethod = true;
    this.open = false;
    return waitForEvent(this.element, "stzhClosed");
  }

  /** Toggle accordion item content */
  @Method()
  async toggle() {
    if (this.open) {
      return await this.hide();
    } else {
      return await this.show();
    }
  }

  /** Focus event */
  @Event() stzhFocus: EventEmitter<StzhAccordionItemFocusEvent>;

  /** Input blur event */
  @Event() stzhBlur: EventEmitter<StzhAccordionItemBlurEvent>;

  @Listen("keydown", { target: "document", capture: true })
  handleKeydown(event: KeyboardEvent) {
    if (event.key === "Shift") {
      this.shiftPressed = true;
    }
  }

  @Listen("keyup", { target: "document", capture: true })
  handleKeyup(event: KeyboardEvent) {
    if (event.key === "Shift") {
      this.shiftPressed = false;
    }
  }

  @State() rootTabindex: number = 0;
  @State() _variant: StzhAccordionVariant;

  private toggledByMethod: boolean = false;
  private bodyElement: HTMLDivElement;
  // @ts-ignore
  private buttonElement: HTMLButtonElement;
  private iconWrapperElement: HTMLDivElement;
  private accordionItemId: string;
  private accordion: HTMLStzhAccordionElement;
  // @ts-ignore
  private focusedByInput: boolean = false;
  private shiftPressed: boolean = false;

  private handleVariantChange = (_event: CustomEvent<StzhAccordionVariantChangeEvent>) => {
    this.variantWatcher(this.variant);
  };

  private handleHeaderClick = () => {
    if (typeof this.collapsible !== "undefined") {
      if (this.collapsible) {
        this.toggle();
      } else if (!this.open) {
        this.show();
      }
    } else if (!this.accordion || this.accordion.collapsible) {
      this.toggle();
    } else if (!this.open) {
      this.show();
    }
  };

  private onFocus = (event: FocusEvent) => {
    this.focusedByInput = true;

    if (this.shiftPressed) {
      // user presses currently shift
      this.rootTabindex = -1;
    }

    this.stzhFocus.emit({
      component: "stzh-accordion-item",
      originalEvent: event,
    });
  };

  private onBlur = (event: FocusEvent) => {
    this.rootTabindex = 0;

    this.stzhBlur.emit({
      component: "stzh-accordion-item",
      originalEvent: event,
    });
  };

  private onButtonKeydown = (event: KeyboardEvent) => {
    let action = null;

    if (event.key === "ArrowDown") {
      action = "next";
    } else if (event.key === "ArrowUp") {
      action = "prev";
    } else if (event.key === "Home") {
      action = "first";
    } else if (event.key === "End") {
      action = "last";
    } else if (event.key === "PageDown" && event.ctrlKey) {
      action = "next";
    } else if (event.key === "PageUp" && event.ctrlKey) {
      action = "prev";
    }

    if (event.key === "Shift") {
      this.rootTabindex = -1;
    }

    if (action) {
      event.preventDefault();
      this.accordion.setItemFocus(this.element, action);
    }
  };

  private onContentKeydown = (event: KeyboardEvent) => {
    if (!event.ctrlKey) {
      return;
    }

    let action = null;

    if (event.key === "PageDown") {
      action = "next";
    } else if (event.key === "PageUp") {
      action = "prev";
    }

    if (action) {
      event.preventDefault();
      this.accordion.setItemFocus(this.element, action);
    }
  };

  componentWillLoad() {
    this.accordionItemId = `stzh-accordion-item-${accordionItemCounter++}`;
  }

  componentDidLoad() {
    this.bodyElement.hidden = !this.open;
    this.bodyElement.style.height = this.open ? "auto" : "0px";
    this.bodyElement.style.overflow = this.open ? "visible" : "hidden";
  }

  async connectedCallback() {
    this.accordion = this.element.closest("stzh-accordion");

    if (this.accordion) {
      this.accordion.addEventListener("stzhVariantChange", this.handleVariantChange);
    }

    this.variantWatcher(this.variant);
  }

  disconnectedCallback() {
    if (this.accordion) {
      this.accordion.removeEventListener("stzhVariantChange", this.handleVariantChange);
      this.accordion = null;
    }
  }

  render() {
    let hasIcon = hasSlot(this.element, "icon") || !!this.icon;
    if (this.iconWrapperElement) {
      hasIcon = hasSlot(this.iconWrapperElement, "icon") || !!this.icon;
    }

    const Heading = `h${this.headingLevel}`;
    const classes = {
      "stzh-accordion-item": true,
      "stzh-accordion-item--has-icon": hasIcon,
      "stzh-accordion-item--has-description": !!this.description,
      "stzh-accordion-item--has-subtitle": !!this.subtitle,
      "stzh-accordion-item--is-open": this.open,
      "stzh-accordion-item--is-disabled": this.disabled,
      [`stzh-accordion-item--${this._variant}`]: !!this._variant,
    };

    return (
      <Host tabindex={this.disabled ? null : this.rootTabindex} is-variant={this._variant}>
        <div class={classes}>
          <Heading
            id={`${this.accordionItemId}-header`}
            class="stzh-accordion-item__heading"
            onClick={this.handleHeaderClick}
          >
            <button
              ref={el => (this.buttonElement = el as HTMLButtonElement)}
              class="stzh-accordion-item__button"
              aria-controls={`${this.accordionItemId}-content`}
              aria-expanded={`${this.open ? "true" : "false"}`}
              disabled={this.disabled}
              onFocus={this.onFocus}
              onBlur={this.onBlur}
              onKeyDown={this.onButtonKeydown}
              type="button"
              s-object-id={this.analyticsId || this.heading}
            >
              <span
                class="stzh-accordion-item__icon-wrapper"
                ref={el => (this.iconWrapperElement = el as HTMLDivElement)}
              >
                {this.icon ? (
                  <stzh-icon class="stzh-accordion-item__icon" name={this.icon}></stzh-icon>
                ) : (
                  <slot name="icon"></slot>
                )}
              </span>
              <span class="stzh-accordion-item__button-text">
                <span class="stzh-accordion-item__subtitle">{this.subtitle}</span>
                <span class="stzh-accordion-item__heading-text">{this.heading}</span>
                <span class="stzh-accordion-item__description">{this.description}</span>
              </span>
              {this._variant === "table" ? (
                <stzh-icon name="angle-down" class="stzh-accordion-item__button-icon is-table"></stzh-icon>
              ) : (
                <span class="stzh-accordion-item__button-icon is-default"></span>
              )}

              {(this.badge || this.badgeEmpty) && (
                <stzh-badge class="stzh-accordion-item__badge" label={this.badge} type={this.badgeType}></stzh-badge>
              )}
            </button>
          </Heading>
          <div
            id={`${this.accordionItemId}-content`}
            ref={el => (this.bodyElement = el as HTMLDivElement)}
            class="stzh-accordion-item__content"
            aria-labelledby={`${this.accordionItemId}-header`}
            role="region"
            onKeyDown={this.onContentKeydown}
          >
            <div class="stzh-accordion-item__content-inner">
              <slot></slot>
            </div>
          </div>
        </div>
      </Host>
    );
  }
}
